import { mediaImageIndexQueryKey, useMediaImageDestroy } from '@jobilla/nexus-sdk'
import ctl from '@netlify/classnames-template-literals'
import { useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { type MouseEvent, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
	ConfirmationDialog,
	type ConfirmationDialogRef,
	emitUnhandledError,
	imgixUrl,
	logger,
	type MediaAsset,
} from '../../../ui'

interface AssetCardProps {
	asset: MediaAsset
	organizationId?: string
	mode: 'page' | 'picker'
	isSelected: boolean
	allowDelete: boolean
	onSelect: () => void
	onDeleted?: () => void
}

export function AssetCard({
	asset,
	organizationId,
	mode,
	isSelected,
	allowDelete,
	onSelect,
	onDeleted,
}: AssetCardProps) {
	const { t } = useTranslation('common')
	const queryClient = useQueryClient()
	const deleteDialogRef = useRef<ConfirmationDialogRef>(null)

	const imageDestroyMutation = useMediaImageDestroy({
		mutationKey: ['media-image-destroy', organizationId, asset.id],
		onSuccess: () => {
			void queryClient.invalidateQueries({
				queryKey: mediaImageIndexQueryKey(),
			})
			onDeleted?.()
		},
		onError: (error: unknown) => {
			logger.error(error, {
				tags: { component: 'AssetCard', action: 'delete-image' },
				data: { assetId: asset.id, organizationId },
			})
			emitUnhandledError({
				title: t('assets.deleteError', 'Failed to delete image — please try again'),
				cause: error,
			})
		},
	})

	const handleDeleteClick = async (e: MouseEvent) => {
		e.preventDefault()
		e.stopPropagation()

		try {
			await deleteDialogRef.current?.confirm({
				title: t('assets.deleteTitle'),
				message: t('assets.deleteMessage'),
				confirmText: t('assets.deleteConfirm'),
				cancelText: t('common.cancel'),
			})
			imageDestroyMutation.mutate({ organisation: organizationId!, media: asset.id })
		} catch {
			// User cancelled
		}
	}

	return (
		<>
			<div
				data-asset-id={asset.id}
				className={ctl(`
					flex flex-col justify-center gap-2 px-2 pt-2 pb-3
					rounded-sm bg-white ring ring-grey-200/20
					shadow-sm
					${
					isSelected
						? '-translate-y-[0.25rem] ring-2 ring-blue-500 bg-blue-50 shadow-md shadow-blue-500/70'
						: ''
				}
					transition-all
					${mode === 'picker' ? 'cursor-pointer' : ''}
				`)}
				onClick={mode === 'picker' ? onSelect : undefined}
			>
				<div
					className="relative"
					style={{
						backgroundColor: 'white',
						backgroundImage:
							'linear-gradient(45deg, var(--color-grey-50) 25%, transparent 25%), linear-gradient(135deg, var(--color-grey-50) 25%, transparent 25%), linear-gradient(45deg, transparent 75%, var(--color-grey-50) 75%), linear-gradient(135deg, transparent 75%, var(--color-grey-50) 75%)',
						backgroundSize: '25px 25px',
						backgroundPosition: '0 0, 12.5px 0, 12.5px -12.5px, 0px 12.5px',
					}}
				>
					<div
						style={{
							backgroundImage: `url("${
								imgixUrl(asset.url, {
									w: 250,
									h: 300,
									fit: 'clip',
									dpr: 2,
								})
							}")`,
							backgroundSize: 'contain',
							backgroundPosition: 'center',
							backgroundRepeat: 'no-repeat',
						}}
						className="w-full h-[300px] bg-repeat"
					/>

					{allowDelete && organizationId
						? (
							<button
								type="button"
								onClick={handleDeleteClick}
								disabled={imageDestroyMutation.isPending}
								className={ctl(`
								absolute bottom-2 right-10
								flex items-center justify-center
								w-6 h-6 rounded-md
								bg-white/70 hover:bg-white hover:bg-red-50 hover:text-red-600
								shadow-sm hover:shadow-md
								text-black/60
								transition-all
								scale-90 hover:scale-100
								cursor-pointer
								appearance-none border-none
								disabled:opacity-50 disabled:cursor-not-allowed
							`)}
								title={t('assets.deleteAssetTooltip')}
							>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									width={14}
									height={14}
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth={2}
									strokeLinecap="round"
									strokeLinejoin="round"
								>
									<polyline points="3 6 5 6 21 6" />
									<path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
								</svg>
							</button>
						)
						: null}

					<a
						href={asset.url}
						target="_blank"
						rel="noopener noreferrer"
						onClick={(e) => e.stopPropagation()}
						className={ctl(`
							absolute bottom-2 right-2
							flex items-center justify-center
							w-6 h-6 rounded-md
							bg-white/70 hover:bg-white
							shadow-sm hover:shadow-md
							text-black/60 hover:text-black
							transition-all
							scale-90 hover:scale-100
						`)}
						title={t('assets.openInNewTab')}
					>
						<svg
							xmlns="http://www.w3.org/2000/svg"
							width={16}
							height={16}
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth={2}
							strokeLinecap="round"
							strokeLinejoin="round"
						>
							<path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
							<polyline points="7 10 12 15 17 10" />
							<line x1="12" y1="15" x2="12" y2="3" />
						</svg>
					</a>
				</div>
				<div className="flex justify-between gap-1">
					<div>
						<div className="text-sm font-medium mb-1">{asset.fileName}</div>
						<div className="text-xs text-grey-500">
							{asset.collection}
							<span className="mx-1 text-grey-300">&bull;</span>
							{asset.width} &times; {asset.height}
							<span className="mx-1 text-grey-400">&bull;</span>
							{asset.humanReadableSize}
						</div>
						<div className="text-xs text-grey-500 whitespace-nowrap">
							{format(asset.createdAt, 'MMM d, yyyy')}
						</div>
					</div>
					<div className="grow-1" />
				</div>
			</div>
			<ConfirmationDialog ref={deleteDialogRef} />
		</>
	)
}
